import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { filter, switchMap } from 'rxjs/operators';
import { MicrosoftGraphService } from '../services/microsoft-graph.service';
import { environment } from '../../environments/environment';
import { MOBILE_BREAKPOINT } from '../constants/app.constants';
import { ListDataService } from '../services/list-data.service';
import { UserDataService } from '../services/user-data.service';
import { UserSettingsService } from '../services/user-settings.service';
import { ScaleViewService } from '../services/scale-view.service';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ScoutAiTriggerService } from '../scout-ai/services/scout-ai-trigger.service';

@Component({
    selector: 'nav-menu',
    templateUrl: 'nav-menu.component.html',
    styleUrls: ['nav-menu.component.css'],
    standalone: false
})
export class NavMenuComponent implements OnInit, OnDestroy {
    private readonly destroyRef = inject(DestroyRef);

    isMobile: boolean = false;
    isMobileSearch:string='';
    loginDisplay: boolean = false;
    userName: string = '';
    errorMessage: string = '';
    userPhotoUrl: string | null = null;
    truckerRateReportUrl: string = '';
    private routerSubscription: Subscription;
    private readonly NAV_DEFER_MS = 2000;
    settingsLoaded: boolean = false;
    environment: string = '';

    constructor(private authService: MsalService, private msalBroadcastService: MsalBroadcastService,
        private microsoftGraph: MicrosoftGraphService, private _listDataService: ListDataService,
        private _userDataService: UserDataService, private _userSettingsService: UserSettingsService,
        private _scaleViewService: ScaleViewService,
        private router: Router, private _titleService: Title,
        private _scoutAiTriggerService: ScoutAiTriggerService) { }

    openScoutAi(): void {
        this._scoutAiTriggerService.requestOpen();
    }

    get isScaleOperator(): boolean {
        return this._userSettingsService.isScaleOperator();
    }

    get isCompuweighEnabled(): boolean {
        return this._userSettingsService.isCompuweighEnabledForCurrentTU();
    }

    ngOnInit(): void {
        if (window.location.href.includes('scout-ai-popout')) return;

        this.routerSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
              this.isMobileSearch = event.url;
            }
          });

        if (window.innerWidth <= MOBILE_BREAKPOINT) {
            this.isMobile = true;
            
        }

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
            )
            .subscribe((result: EventMessage) => {
            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None)
            )
            .subscribe(() => {
                this.setLoginDisplay();
            });

        this._userSettingsService.locCodeChanged$
            .pipe(
                switchMap(locCode => this._scaleViewService.resolveCompuweighEnabled(locCode)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(enabled => {
                this._userSettingsService.setCompuweighEnabled(enabled);
            });
    }

    setLoginDisplay() {
        this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;

        if (this.loginDisplay) {
            this.GetEnvironment();
            this._userDataService.getUserSettings().subscribe({
                next: settings => {
                    this._userSettingsService.setUserSettings(settings);
                    this.resolveCompuweighEnabled(settings.defaultLocCode);
                }
            });
            setTimeout(() => {
                this.getUsername();
                this.getUserPhoto();
                this.getTruckerRateReport();
            }, this.NAV_DEFER_MS);
        }
    }

    private resolveCompuweighEnabled(defaultLocCode: string): void {
        this._scaleViewService.resolveCompuweighEnabled(defaultLocCode)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(enabled => {
                this._userSettingsService.setCompuweighEnabled(enabled);
                this.settingsLoaded = true;
            });
    }

    logout() {
        this.authService.logoutRedirect({
            postLogoutRedirectUri: environment.baseUri
        });
    }

    getUsername() {
        this.microsoftGraph.getUserProfile().subscribe({
            next: data => {
                this.userName = data.displayName;
            },
            error: err => this.errorMessage = err
        });
    }

    getUserPhoto() {
        this.microsoftGraph.getUserPhoto().subscribe({
            next: blob => {
                const imageUrl = URL.createObjectURL(blob);
                this.userPhotoUrl = imageUrl;
            },
            error: err => this.errorMessage = err
        });
    }

    getTruckerRateReport() {
        this._listDataService.getTruckerRateReportUrl().subscribe({
            next: data => {
                this.truckerRateReportUrl = data;
            },
            error: err => this.errorMessage = err
        });

    }

    ngOnDestroy() {
        // Unsubscribe to prevent memory leaks
        if (this.routerSubscription) {
          this.routerSubscription.unsubscribe();
        }
      }

    GetEnvironment() {
        this._listDataService.getEnvironment().subscribe({
            next: result => {
                this.environment = result;
                if (result) {
                    this._titleService.setTitle('Scout - ' + result);
                }
            },
            error: err => this.errorMessage = err
        });
    }   

    
}
